import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import ManWithPhone from "@/assets/Img/man-with-phone.jpg";
import PeopleSunshine from "@/assets/Img/people-sunshine.jpg";
import CallIcon from "@/assets/Icons/call.svg?react";
import ChatIcon from "@/assets/Icons/chat.svg?react";
import BookIcon from "@/assets/Icons/book.svg?react";
import CopyPagesIcon from "@/assets/Icons/copy-pages.svg?react";
import { ContactCard } from "@/components/ContactCard";
import { SalesforceContactForm } from "@/components/SalesforceContactForm";
import { BusinessFaqs } from "@/components/BusinessFaqs";
import { CopyTextToClipboard } from "@/utilities/CopyTextToClipboard";
import { isMobile } from "react-device-detect";
import { useRef, useState } from "react";
import { CheckIcon } from "@heroicons/react/16/solid";

const CONTACT_PHONE = "+17135815377";
const WHATSAPP_PHONE = "18327556099";

export const BusinessContact = () => {
  const [tBusiness] = useTranslation("business");
  const salesforceFormRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = (text: string) => {
    CopyTextToClipboard(text);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <>
      <Helmet>
        <meta httpEquiv="Content-type" content="text/html; charset=UTF-8" />
        <script
          dangerouslySetInnerHTML={{
            __html: `function timestamp() { var response = document.getElementById("g-recaptcha-response"); if (response == null || response.value.trim() == "") {var elems = JSON.parse(document.getElementsByName("captcha_settings")[0].value);elems["ts"] = JSON.stringify(new Date().getTime());document.getElementsByName("captcha_settings")[0].value = JSON.stringify(elems); } } setInterval(timestamp, 500);`,
          }}
        ></script>
      </Helmet>
      <div className="w-full flex flex-col">
        <div className="w-full h-52 md:h-64 xl:h-72 relative">
          <img
            className="w-full h-full object-cover object-right max-w-screen-2xl absolute right-0 top-0"
            src={ManWithPhone}
          />
          <div className="absolute left-0 top-0 w-full h-full from-30% md:from-50% [@media(min-width:2000px)]:from-70% bg-gradient-to-r from-black to-black/40 flex justify-start">
            <div className="w-full md:w-7/12 flex flex-col items-start md:items-center justify-center px-6 md:px-10">
              <div className="w-full lg:w-2/3">
                <p className="text-sendolab-white2 font-medium text-3xl lg:text-4xl min-w-max">
                  {tBusiness("Contact.HeadingTitle")}
                </p>
                <p className="text-sendolab-white2 font-light text-sm sm:text-lg md:text-xl xl:text-2xl mt-2">
                  {tBusiness("Contact.HeadingSubtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex items-center justify-center h-24 px-6 md:px-10 bg-sendolab-gray2">
          <p className="text-business-green font-light text-sm sm:text-base lg:text-xl max-w-screen-sm md:max-w-screen-md text-center">
            {tBusiness("Contact.Questions")}
          </p>
        </div>

        <div className="w-full flex flex-col gap-16 py-16 px-6 md:px-10 bg-sendolab-white2 items-center">
          <div className="inline-flex flex-wrap justify-center gap-x-12 gap-y-6 max-w-screen-lg">
            <ContactCard
              icon={<CallIcon className="w-10 h-10 text-business-green" />}
              title={tBusiness("Contact.ContactCards.Card1.Title")}
              text={tBusiness("Contact.ContactCards.Card1.Text")}
              buttonText={tBusiness("Contact.ContactCards.Card1.Button")}
              buttonIcon={
                !isMobile && !isCopied ? (
                  <CopyPagesIcon className="text-white h-5 w-5 animate-fade-in" />
                ) : !isMobile && isCopied ? (
                  <CheckIcon className="text-white h-5 w-5 animate-fade-in" />
                ) : undefined
              }
              link={isMobile ? `tel:${CONTACT_PHONE}` : undefined}
              onClick={!isMobile ? () => handleCopy(CONTACT_PHONE) : undefined}
            />
            <ContactCard
              icon={<BookIcon className="w-10 h-10 text-business-green" />}
              title={tBusiness("Contact.ContactCards.Card2.Title")}
              text={tBusiness("Contact.ContactCards.Card2.Text")}
              buttonText={tBusiness("Contact.ContactCards.Card2.Button")}
              onClick={() =>
                salesforceFormRef.current?.scrollIntoView({
                  behavior: "smooth",
                })
              }
            />
            <ContactCard
              icon={<ChatIcon className="w-10 h-10 text-business-green" />}
              title={tBusiness("Contact.ContactCards.Card3.Title")}
              text={tBusiness("Contact.ContactCards.Card3.Text")}
              buttonText={tBusiness("Contact.ContactCards.Card3.Button")}
              onClick={() => {
                window.open(`https://wa.me/${WHATSAPP_PHONE}`, "_blank");
              }}
            />
          </div>
          <div
            ref={salesforceFormRef}
            className="bg-white rounded-3xl shadow-lg flex justify-items-center items-center flex-col gap-4 w-full max-w-screen-md p-8"
          >
            <SalesforceContactForm />
          </div>
        </div>

        <div className="w-full flex flex-col items-center pt-10 pb-20 px-6 lg:px-10 bg-gradient-to-r from-black via-sendolab-gray2 to-black">
          <div className="flex flex-col w-full items-center gap-8 sm:gap-10 max-w-screen-xl sm:px-10 pt-10 sm:py-10">
            <div className="flex flex-col gap-4">
              <p
                className="text-white text-3xl md:text-4xl font-medium text-center inner-span-semibold px-6 lg:px-10"
                dangerouslySetInnerHTML={{
                  __html: tBusiness("Contact.WeAreHere"),
                }}
              />
              <p
                className="text-sendolab-white text-xl md:text-2xl font-[300] text-center inner-span-semibold px-6 lg:px-10"
                dangerouslySetInnerHTML={{
                  __html: tBusiness("Contact.Available"),
                }}
              />
            </div>
            <div className="relative flex items-center justify-center w-full h-52 sm:h-96 rounded-3xl overflow-hidden sm:mt-10">
              <img
                className="absolute w-full h-full top-0 left-0 object-cover object-top"
                src={PeopleSunshine}
              />
              <div className="absolute top-0 left-0 py-8 px-8 sm:px-14 w-full h-full bg-black/50 flex items-center justify-center">
                <p className="text-white text-lg md:text-2xl lg:text-3xl text-center">
                  {tBusiness("TrustAndTransparency.Paragraph2")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          id="faqs"
          className="w-full sm:bg-black bg-gradient-to-r from-black via-sendolab-gray4 to-black py-20 px-10 md:px-20 flex justify-center"
        >
          <BusinessFaqs />
        </div>
      </div>
    </>
  );
};
